import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Modal from '../../../../shared/_TBComponents/Modal';
import Icon from '../../../../shared/_TBComponents/Icon';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import useWindowSize from '../../../../shared/hooks/useWindowSize';
import ProgramTemplatePreview, {
  DB_IMPORTED, TagChip, tagSolid, formatStats,
} from './ProgramTemplatePreview';
import TagFilterDropdown from './TagFilterDropdown';
import {
  fetchProgramTemplates,
  fetchProgramTemplateTags,
  fetchProgramTemplateDetail,
  transferProgramTemplate,
  cancelProgramTemplateTransfer,
  fetchSavedWorkouts,
} from '../../../calendar/ducks/calendarActions';
import { fetchPrograms } from '../../ducks/navigatorActions';

// Imported program's exercises land in the Imported holding-pen (see the
// Exercises tab) unless the coach picks the Master database.

const TAG_FALLBACK = '#9E9E9E';

const REQUEST_PROGRAM_URL = 'https://teambuildr.userjot.com/board/program-templates?openFeedback';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 24px 0;
  font-family: "Nunito Sans";
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px #D6D6D6;

  @media (max-width: 767px) {
    gap: 12px;
    padding: 16px 0;
  }

  /* Only short viewports (landscape/split-view) get a max-height + scroll — a cap
     on portrait phones would clip the Tags popover. svh over vh: iOS Safari's vh ignores toolbar chrome. */
  @media (max-width: 767px) and (max-height: 620px) {
    max-height: calc(100vh - 60px);
    max-height: calc(100svh - 60px);
    overflow-y: auto;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;

  @media (max-width: 767px) {
    padding: 0 16px;
  }
`;

const Title = styled.h2`
  margin: 0;
  font-family: "Nunito Sans";
  font-size: 24px;
  font-weight: 900;
  letter-spacing: -0.24px;
  color: #1A1A1A;

  @media (max-width: 767px) {
    font-size: 20px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const Divider = styled.div`
  height: 1px;
  background: #D6D6D6;
  width: 100%;
`;

const ListSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 24px;

  @media (max-width: 767px) {
    gap: 12px;
    padding: 0 16px;
  }
`;

const IntroGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionTitle = styled.span`
  font-family: "Nunito Sans";
  font-size: 20px;
  font-weight: 700;
  line-height: 1.5;
  color: #1A1A1A;
`;

const SectionSubtitle = styled.span`
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #1A1A1A;

  @media (max-width: 767px) {
    display: none;
  }
`;

/* Search and the Tags filter share a row on phones; on desktop the Tags
   button is hidden and search takes the full width on its own. */
const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const SearchField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding: 6px 16px;
  border: 1px solid #D6D6D6;
  border-radius: 4px;
  background: #FFFFFF;

  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    padding: 0;
    font-family: "Nunito Sans";
    font-size: 14px;
    line-height: 1.5;
    color: #1A1A1A;

    &::placeholder { color: #929292; }
  }

  /* Match the Tags button's height so the shared row reads as one control. */
  @media (max-width: 767px) {
    height: 40px;
    padding: 6px 12px;
  }
`;

const ListBox = styled.div`
  display: flex;
  gap: 8px;
  height: 354px;
  width: 100%;
  border: 1px solid #D6D6D6;
  border-radius: 4px;
  background: #FFFFFF;
  overflow: hidden;

  /* svh keeps the box a constant height per phone (vh/dvh shift as iOS's toolbar
     collapses); the subtrahend budgets for chrome plus the shared Modal's variable padding-bottom. Plain px is the svh-unsupported fallback. */
  @media (max-width: 767px) {
    height: 180px;
    height: clamp(130px, calc(100svh - 424px), 320px);
  }
`;

/* Sized to the widest realistic tag name so it shows in full; the cap keeps a
   pathological tag from squeezing the program list — it wraps instead. */
const TagRail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: max-content;
  min-width: 152px;
  max-width: 280px;
  flex: none;
  padding: 8px 16px;
  border-right: 1px solid #D6D6D6;
  box-shadow: 4px 0 20px 0 rgba(48, 49, 51, 0.10);
  overflow-y: auto;

  @media (max-width: 767px) {
    display: none;
  }
`;

/* On phones the side-by-side rail would squeeze the program list, so the tag
   filter collapses into the Tags-button dropdown used on Manage Programs. */
const MobileTagFilter = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: flex;
  }
`;

const TagRailHeading = styled.span`
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  color: #1A1A1A;
`;

const TagRow = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  text-align: left;

  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

// Unselected: 2px outlined ring in the tag color. Selected: filled disc with
// a white checkmark.
const TagDot = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 100px;
  flex-shrink: 0;
  box-sizing: border-box;
  border: ${(props) => (props.active ? 'none' : `2px solid ${props.dotColor}`)};
  background: ${(props) => (props.active ? props.dotColor : 'transparent')};
`;

/* Constant weight — the filled dot marks the active tag; a weight flip would
   resize the max-content rail and shift the layout. */
const TagName = styled.span`
  font-family: "Nunito Sans";
  min-width: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #1A1A1A;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProgramList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-width: 0;
  padding: 8px 0;
  overflow-y: auto;
`;

const ProgramRow = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  flex-shrink: 0;

  &:hover { background: #FAFAFA; }
  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const ProgramRowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const ProgramName = styled.span`
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  color: #1A1A1A;
  overflow-wrap: anywhere;
`;

const ProgramMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  flex-wrap: wrap;
`;

const ProgramStats = styled.span`
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #1A1A1A;
  white-space: nowrap;
`;

const Centered = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #9E9E9E;
  font-size: 16px;
  text-align: center;
  padding: 32px 16px;
`;

const RequestCallout = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  border-radius: 4px;
  background: #EAF2FF;
  font-family: "Nunito Sans";
  font-size: 16px;
  line-height: 1.5;
  color: #1A1A1A;

  a {
    font-size: 16px;
    font-weight: 700;
    color: #1787FB;
    text-decoration: underline;

    &:hover { color: #1787FB; }
  }

  /* Phones don't have the width for prompt and link side by side. */
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
`;

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.previewStep ? 'flex-end' : 'flex-start')};
  gap: 8px;
  padding: 0 24px;
  min-height: 36px;

  @media (max-width: 767px) {
    padding: 0 16px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  padding: 6px 16px;
  border: none;
  border-radius: 4px;
  background: none;
  cursor: pointer;
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.48px;
  color: #1A1A1A;

  &:hover { opacity: 0.7; }
  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  padding: 6px 16px;
  border: none;
  border-radius: 4px;
  background: #1A1A1A;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.48px;
  color: #FFFFFF;

  &:hover { opacity: ${(props) => (props.disabled ? 1 : 0.85)}; }
  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const ProgramTemplatesModal = ({
  isOpen, onClose, onBack, accountCode,
}) => {
  const dispatch = useDispatch();
  const { width: windowWidth } = useWindowSize();
  const isMobile = windowWidth <= 767;
  const [step, setStep] = useState('list');
  const [templates, setTemplates] = useState([]);
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTagIds, setActiveTagIds] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detailItems, setDetailItems] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  // Which exercise database transferred exercises land in (targetExerciseDatabase).
  const [destination, setDestination] = useState(DB_IMPORTED);

  // The modal stays mounted across open/close, so closing must cancel any
  // in-flight import poll — otherwise its callbacks fire into the next open.
  useEffect(() => {
    if (!isOpen) {
      dispatch(cancelProgramTemplateTransfer());
      setIsImporting(false);
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    setStep('list');
    setSelected(null);
    setSearch('');
    setActiveTagIds([]);
    setDestination(DB_IMPORTED);
    dispatch(fetchProgramTemplateTags({ onSuccess: (data) => setTags(data || []) }));
    dispatch(fetchProgramTemplates([], {
      onSuccess: (data) => { setTemplates(data || []); setIsLoading(false); },
      onError: () => setIsLoading(false),
    }));
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (!selected) { setDetailItems(null); return; }
    setIsDetailLoading(true);
    setDetailItems(null);
    dispatch(fetchProgramTemplateDetail(selected.id, {
      onSuccess: (data) => {
        const list = Array.isArray(data) ? data : [data];
        const items = list.flatMap((d) => (d && d.programItems) || []);
        setDetailItems(items);
        setIsDetailLoading(false);
      },
      onError: () => setIsDetailLoading(false),
    }));
  }, [selected, dispatch]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (templates || []).filter((t) => {
      const matchesSearch = !q
        || t.programName?.toLowerCase().includes(q)
        || (t.tags || []).some((tag) => tag.name?.toLowerCase().includes(q));
      const matchesTags = activeTagIds.length === 0
        || (t.tags || []).some((tag) => activeTagIds.includes(tag.id));
      return matchesSearch && matchesTags;
    });
  }, [templates, search, activeTagIds]);

  // Selected tags float to the top of the rail.
  const orderedTags = useMemo(() => [
    ...tags.filter((t) => activeTagIds.includes(t.id)),
    ...tags.filter((t) => !activeTagIds.includes(t.id)),
  ], [tags, activeTagIds]);

  const toggleTag = (id) => setActiveTagIds((prev) => (
    prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
  ));

  const selectTemplate = (t) => {
    setSelected(t);
    setStep('preview');
  };

  const handleImport = () => {
    if (!selected) return;
    setIsImporting(true);
    dispatch(transferProgramTemplate(
      {
        targetAccountCode: accountCode,
        savedWorkoutId: selected.id,
        targetExerciseDatabase: destination,
      },
      {
        onSuccess: () => {
          // onSuccess fires once the transfer pipeline actually finishes, so
          // the refetch lands the new program in Manage Programs immediately.
          // The Programs rail reads a separate slice and this modal opens in
          // place (no remount to refresh it), so both need refetching.
          dispatch(fetchSavedWorkouts(accountCode));
          dispatch(fetchPrograms(accountCode));
          setIsImporting(false);
          onClose();
        },
        onError: () => setIsImporting(false),
      },
    ));
  };

  const weeks = useMemo(() => {
    if (!detailItems) return [];
    const byWeek = {};
    detailItems.forEach((item) => {
      const wk = item.weekNumber || 1;
      if (!byWeek[wk]) byWeek[wk] = [];
      byWeek[wk].push(item);
    });
    return Object.keys(byWeek)
      .map((wk) => parseInt(wk, 10))
      .sort((a, b) => a - b)
      .map((wk) => ({ week: wk, days: byWeek[wk] }));
  }, [detailItems]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      hideButton
      hideTitle
      customMaxWidth='750px'
      customMinHeight='0'
      customPadding='0'
      customBorderRadius='8px'
    >
      <Body>
        <HeaderRow>
          <Title>{step === 'preview' ? 'Preview and Download' : 'Program Templates'}</Title>
          <CloseButton type='button' onClick={onClose} aria-label='Close'>
            <Icon icon='remove' size={16} color='#929292' />
          </CloseButton>
        </HeaderRow>

        <Divider />

        {step === 'list' ? (
          <ListSection>
            <IntroGroup>
              <SectionTitle>Select a program</SectionTitle>
              <SectionSubtitle>
                Select the program you&rsquo;d like to preview and customize it later.
              </SectionSubtitle>
            </IntroGroup>

            <SearchRow>
              <SearchField>
                <Icon icon='magnifying-glass' size={isMobile ? 18 : 24} color='#929292' />
                <input
                  name='template-search'
                  placeholder={isMobile ? 'Search for programs' : 'Search for programs or tags'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </SearchField>

              <MobileTagFilter>
                <TagFilterDropdown
                  tags={tags}
                  selectedIds={activeTagIds}
                  onChange={setActiveTagIds}
                  alignRight
                />
              </MobileTagFilter>
            </SearchRow>

            <ListBox>
              <TagRail>
                <TagRailHeading>Filter by tags</TagRailHeading>
                {orderedTags.map((tag) => {
                  const active = activeTagIds.includes(tag.id);
                  return (
                    <TagRow
                      key={tag.id}
                      type='button'
                      onClick={() => toggleTag(tag.id)}
                      aria-pressed={active}
                    >
                      <TagDot active={active} dotColor={tag.color || TAG_FALLBACK}>
                        {active && (
                          <svg width='11' height='8' viewBox='0 0 10.6939 8.02926' fill='none' aria-hidden='true'>
                            <path fillRule='evenodd' clipRule='evenodd' fill='#FFFFFF' d='M3.90738 8.02926V8.02926C3.7226 8.02867 3.54636 7.95141 3.42071 7.81593L0.18071 4.36926H0.18071C-0.0714999 4.10048 -0.0580686 3.67814 0.21071 3.42593C0.479489 3.17372 0.90183 3.18715 1.15404 3.45593V3.45593L3.90071 6.38259L9.50738 0.249261V0.249261C9.73792 -0.0378191 10.1575 -0.0836545 10.4446 0.146886C10.7317 0.377426 10.7775 0.79704 10.547 1.08412C10.5305 1.10464 10.5128 1.12416 10.494 1.14259L4.40071 7.80926V7.80926C4.27623 7.94721 4.09983 8.02695 3.91404 8.02926L3.90738 8.02926Z' />
                          </svg>
                        )}
                      </TagDot>
                      <TagName>{tag.name}</TagName>
                    </TagRow>
                  );
                })}
              </TagRail>

              <ProgramList>
                {isLoading && <Centered><Spinner /></Centered>}
                {!isLoading && visible.length === 0 && (
                  <Centered>No matching templates</Centered>
                )}
                {!isLoading && visible.map((t) => (
                  <ProgramRow
                    key={t.id}
                    type='button'
                    onClick={() => selectTemplate(t)}
                  >
                    <ProgramRowMain>
                      <ProgramName>{t.programName || 'Untitled template'}</ProgramName>
                      <ProgramMetaRow>
                        <ProgramStats>{formatStats(t)}</ProgramStats>
                        {(t.tags || []).map((tag) => {
                          const solid = tagSolid(tag.color);
                          return (
                            <TagChip key={tag.id} tintColor={solid.fg} tintBg={solid.bg}>
                              {tag.name}
                            </TagChip>
                          );
                        })}
                      </ProgramMetaRow>
                    </ProgramRowMain>
                    <Icon icon='right-arrow' size={16} color='#929292' />
                  </ProgramRow>
                ))}
              </ProgramList>
            </ListBox>

            <RequestCallout>
              <span>Don&rsquo;t see a program you want?</span>
              <a href={REQUEST_PROGRAM_URL} target='_blank' rel='noopener noreferrer'>
                Request a program.
              </a>
            </RequestCallout>
          </ListSection>
        ) : (
          <ProgramTemplatePreview
            template={selected}
            weeks={weeks}
            isDetailLoading={isDetailLoading}
            destination={destination}
            setDestination={setDestination}
          />
        )}

        <Divider />

        <FooterBar previewStep={step === 'preview'}>
          {step === 'list' ? (
            <BackButton type='button' onClick={onBack || onClose}>
              <Icon icon='left-arrow' size={12} color='#1A1A1A' />
              Back
            </BackButton>
          ) : (
            <>
              <BackButton type='button' onClick={() => setStep('list')}>
                <Icon icon='left-arrow' size={12} color='#1A1A1A' />
                Back
              </BackButton>
              <DownloadButton
                type='button'
                disabled={isImporting}
                onClick={handleImport}
              >
                Download Template
                {isImporting
                  ? <Spinner large={false} saving />
                  : <Icon icon='download' size={20} color='#FFFFFF' />}
              </DownloadButton>
            </>
          )}
        </FooterBar>
      </Body>
    </Modal>
  );
};

ProgramTemplatesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  accountCode: PropTypes.number,
};

ProgramTemplatesModal.defaultProps = {
  onBack: undefined,
  accountCode: undefined,
};

export default ProgramTemplatesModal;
